import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Upload, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function SeasonalRecommendationsManagement() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    image_url: "",
    description: "",
    start_date: "",
    end_date: "",
    price: "",
    weight_per_unit: "",
    unit: "g",
    product_id: "",
    is_active: true,
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/seasonal-recommendations`, getAuthHeaders());
      setRecommendations(res.data || []);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditing(null);
    setFormData({
      title: "",
      image_url: "",
      description: "主な産地：\nサイズ：\n発注単位：\n価格目安：\n１つあたりの重量：",
      start_date: "",
      end_date: "",
      price: "",
      weight_per_unit: "",
      unit: "g",
      product_id: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditing(rec);
    setFormData({
      title: rec.title || "",
      image_url: rec.image_url || "",
      description: rec.description || "",
      start_date: rec.start_date || "",
      end_date: rec.end_date || "",
      price: rec.price?.toString() || "",
      weight_per_unit: rec.weight_per_unit?.toString() || "",
      unit: rec.unit || "g",
      product_id: rec.product_id || "",
      is_active: rec.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const res = await axios.post(
        `${API}/admin/seasonal-recommendations/upload-image`,
        formDataUpload,
        getAuthHeaders()
      );

      setFormData({ ...formData, image_url: res.data.image_url });
      toast.success("画像をアップロードしました");
    } catch (error) {
      toast.error("画像のアップロードに失敗しました");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.start_date || !formData.end_date) {
      toast.error("必須項目を入力してください");
      return;
    }

    try {
      const payload = {
        title: formData.title,
        image_url: formData.image_url || null,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        product_id: formData.product_id || null,
        is_active: formData.is_active,
      };

      if (editing) {
        await axios.put(
          `${API}/admin/seasonal-recommendations/${editing.id}`,
          payload,
          getAuthHeaders()
        );
        toast.success("更新しました");
      } else {
        await axios.post(
          `${API}/admin/seasonal-recommendations`,
          payload,
          getAuthHeaders()
        );
        toast.success("作成しました");
      }

      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = "保存に失敗しました";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d.msg || String(d)).join(", ");
      }
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      await axios.delete(`${API}/admin/seasonal-recommendations/${id}`, getAuthHeaders());
      toast.success("削除しました");
      fetchAll();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  if (loading) {
    return <div className="text-center py-8">読み込み中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-600" />
            季節のおすすめ管理
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            季節限定商品のおすすめ情報を管理します
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4 mr-1" />
          新規作成
        </Button>
      </div>

      {/* グリッド表示 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recommendations.length === 0 ? (
          <div className="col-span-full bg-slate-50 border border-slate-200 rounded-lg p-8 text-center text-slate-500">
            季節のおすすめがありません
          </div>
        ) : (
          recommendations.map((rec) => (
            <div
              key={rec.id}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              {rec.image_url && (
                <img
                  src={`${BACKEND_URL}${rec.image_url}`}
                  alt={rec.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <h3 className="font-bold text-lg text-slate-800 mb-2">
                  {rec.title}
                </h3>
                <div className="text-sm text-slate-600 mb-3 line-clamp-3 whitespace-pre-line">
                  {rec.description}
                </div>
                <div className="space-y-1 text-xs text-slate-500">
                  <div>期間: {rec.start_date} 〜 {rec.end_date}</div>
                  <div>
                    状態:{" "}
                    <span
                      className={`font-medium ${
                        rec.is_active ? "text-emerald-600" : "text-slate-400"
                      }`}
                    >
                      {rec.is_active ? "有効" : "無効"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(rec)}
                    className="flex-1"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    編集
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(rec.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 作成・編集ダイアログ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "季節のおすすめを編集" : "季節のおすすめを作成"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>タイトル *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="例: 旬の春野菜セット"
                className="mt-1"
              />
            </div>

            <div>
              <Label>画像</Label>
              <div className="mt-1 space-y-2">
                {formData.image_url && (
                  <img
                    src={`${BACKEND_URL}${formData.image_url}`}
                    alt="プレビュー"
                    className="w-full h-48 object-cover rounded border"
                  />
                )}
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="flex-1"
                  />
                  {uploading && <span className="text-sm text-slate-500">アップロード中...</span>}
                </div>
              </div>
            </div>

            <div>
              <Label>詳細説明 *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="商品の詳細を入力してください"
                rows={8}
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                ※デフォルトで表示される項目を自由に編集できます
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>開始日 *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>終了日 *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                有効にする
              </Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
              {editing ? "更新" : "作成"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
